import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import debounce from 'lodash/debounce';
import store from '@/store';
import { config } from '@/config.js';
import { useRoute, useRouter } from '@/composables/common/router/useRouter';
import { useToast } from '@/composables/common/useToast';

export function useResumenBases() {
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();

    const bases = ref({ data: [], meta: {} });
    const loading = ref(false);

    const filters = ref({
        search: '',
        page: 1,
        per_page: 10
    });

    const meta = computed(() => bases.value.meta || {});

    const listaId = computed(() => route.params.lista_id);
    const categoriaId = computed(() => route.params.categoria_id);

    // --- Data fetching ---

    async function fetch() {
        const f = filters.value;
        const params = {
            filters: {
                search: f.search,
                page: f.page,
                per_page: f.per_page
            },
            lista_id: listaId.value,
            categoria_id: categoriaId.value
        };

        loading.value = true;
        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.bases.categorias.resumen.bases'),
                params
            );
            bases.value = res.data;
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') console.error('Error fetching resumen bases:', error);
        } finally {
            loading.value = false;
        }
    }

    // --- Delete ---

    async function borrar(baseId) {
        const params = {
            lista_id: listaId.value,
            base_id: baseId,
            filters: {}
        };

        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.bases.borrar'),
                params
            );
            toast.success(res.data.message || 'Deportista eliminado correctamente');
            fetch();
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Error al borrar el deportista');
        }
    }

    // --- URL Sync ---

    const isHydrating = ref(false);

    function updateUrlQuery() {
        const f = filters.value;
        const query = {};

        if (f.search) query.search = f.search;
        if (f.page && f.page > 1) query.page = String(f.page);
        if (f.per_page && f.per_page !== 10) query.per_page = String(f.per_page);

        router.replace({ query }).catch(() => {});
    }

    const debouncedFetch = debounce(() => fetch(), 500);
    const updateUrlDebounced = debounce(() => updateUrlQuery(), 500);

    // --- Watchers ---

    watch(filters, () => {
        if (isHydrating.value) return;
        debouncedFetch();
        updateUrlDebounced();
    }, { deep: true });

    // --- Lifecycle ---

    onMounted(() => {
        const query = route.query;
        const queryKeys = Object.keys(query);
        if (queryKeys.length > 0) {
            isHydrating.value = true;
            if (query.search) filters.value.search = query.search;
            if (query.page) filters.value.page = parseInt(query.page, 10);
            if (query.per_page) filters.value.per_page = parseInt(query.per_page, 10);
            nextTick(() => { isHydrating.value = false; });
        }

        fetch();
    });

    onUnmounted(() => {
        debouncedFetch.cancel();
        updateUrlDebounced.cancel();
    });

    return {
        bases,
        loading,
        filters,
        meta,
        listaId,
        fetch,
        debouncedFetch,
        borrar
    };
}
